/**
 * ID Documents Checklist Component
 *
 * Displays and allows approval/rejection of identification documents uploaded by recruits.
 * Documents are uploaded via the Frontfacing app and stored in Supabase Storage.
 *
 * Features:
 * - Per-ID approve/reject buttons
 * - Rejection requires reason (20-500 chars)
 * - Shows rejection status with reason
 * - Task completes when BOTH IDs are approved
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Info, Loader2, CheckCircle, Clock, FileText, ExternalLink, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { getIDLabel, type IDType, type IDStatus } from '@/types/identification';
import { approveRecruitId } from '@/services/recruitmentService';
import { RejectIDDialog } from './RejectIDDialog';

interface IDDocumentsChecklistProps {
  recruitId: string;
  onTaskComplete?: () => void;
}

interface OtherDocument {
  id: string;
  label: string;
  file_path: string;
  file_name: string;
  uploaded_at: string;
}

interface IDDocumentData {
  // Primary ID
  primaryType: IDType | null;
  primaryFrontPath: string | null;
  primaryBackPath: string | null;
  primaryStatus: IDStatus | null;
  primaryApprovedAt: string | null;
  primaryRejectedAt: string | null;
  primaryRejectionReason: string | null;

  // Secondary ID
  secondaryType: IDType | null;
  secondaryFrontPath: string | null;
  secondaryBackPath: string | null;
  secondaryStatus: IDStatus | null;
  secondaryApprovedAt: string | null;
  secondaryRejectedAt: string | null;
  secondaryRejectionReason: string | null;

  // Overall validation
  validatedAt: string | null;
  validatedBy: string | null;

  // Recruit info for dialog
  firstName: string;
  lastName: string;
  email: string;

  // Other docs
  otherDocuments: OtherDocument[];
}

export const IDDocumentsChecklist: React.FC<IDDocumentsChecklistProps> = ({
  recruitId,
  onTaskComplete
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<'primary' | 'secondary' | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingIdType, setRejectingIdType] = useState<'primary' | 'secondary'>('primary');
  const [data, setData] = useState<IDDocumentData>({
    primaryType: null,
    primaryFrontPath: null,
    primaryBackPath: null,
    primaryStatus: null,
    primaryApprovedAt: null,
    primaryRejectedAt: null,
    primaryRejectionReason: null,
    secondaryType: null,
    secondaryFrontPath: null,
    secondaryBackPath: null,
    secondaryStatus: null,
    secondaryApprovedAt: null,
    secondaryRejectedAt: null,
    secondaryRejectionReason: null,
    validatedAt: null,
    validatedBy: null,
    firstName: '',
    lastName: '',
    email: '',
    otherDocuments: []
  });

  // Load ID document data from database
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when recruitId changes
  }, [recruitId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: recruit, error } = await supabase
        .from('recruits')
        .select(`
          first_name,
          last_name,
          email,
          id_primary_type,
          id_primary_front_path,
          id_primary_back_path,
          id_primary_status,
          id_primary_approved_at,
          id_primary_rejected_at,
          id_primary_rejection_reason,
          id_secondary_type,
          id_secondary_front_path,
          id_secondary_back_path,
          id_secondary_status,
          id_secondary_approved_at,
          id_secondary_rejected_at,
          id_secondary_rejection_reason,
          id_validated_at,
          id_validated_by,
          other_documents
        `)
        .eq('id', recruitId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      // Handle case where RLS blocks access (returns null)
      if (!recruit) {
        toast.error('Unable to load recruit data. You may not have permission to view this recruit.');
        return;
      }

      setData({
        firstName: recruit.first_name,
        lastName: recruit.last_name,
        email: recruit.email,
        primaryType: recruit.id_primary_type as IDType,
        primaryFrontPath: recruit.id_primary_front_path,
        primaryBackPath: recruit.id_primary_back_path,
        primaryStatus: recruit.id_primary_status as IDStatus,
        primaryApprovedAt: recruit.id_primary_approved_at,
        primaryRejectedAt: recruit.id_primary_rejected_at,
        primaryRejectionReason: recruit.id_primary_rejection_reason,
        secondaryType: recruit.id_secondary_type as IDType,
        secondaryFrontPath: recruit.id_secondary_front_path,
        secondaryBackPath: recruit.id_secondary_back_path,
        secondaryStatus: recruit.id_secondary_status as IDStatus,
        secondaryApprovedAt: recruit.id_secondary_approved_at,
        secondaryRejectedAt: recruit.id_secondary_rejected_at,
        secondaryRejectionReason: recruit.id_secondary_rejection_reason,
        validatedAt: recruit.id_validated_at,
        validatedBy: recruit.id_validated_by,
        otherDocuments: (recruit.other_documents as OtherDocument[]) || []
      });
    } catch {
      toast.error('Failed to load identification documents');
    } finally {
      setLoading(false);
    }
  };

  // Get signed URL for document viewing
  const getDocumentUrl = async (path: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('recruit-identifications')
        .createSignedUrl(path, 3600);

      if (error) {
        throw error;
      }

      return data.signedUrl;
    } catch {
      toast.error('Failed to get document URL');
      return null;
    }
  };

  // Open document in new tab
  const handleViewDocument = async (path: string | null) => {
    if (!path) {
return;
}

    const url = await getDocumentUrl(path);
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Approve an ID
  const handleApprove = async (idType: 'primary' | 'secondary') => {
    if (!user?.id) {
      toast.error('Authentication required');
      return;
    }

    setApproving(idType);
    try {
      const result = await approveRecruitId(recruitId, idType, user.id);

      if (result.success) {
        await loadData();
        toast.success(`${idType === 'primary' ? 'Primary' : 'Secondary'} ID approved`);

        if (result.bothApproved) {
          toast.success('Both IDs approved! Task complete.', {
            description: 'Identification verification is now complete.'
          });
          onTaskComplete?.();
        }
      }
    } catch {
      toast.error('Failed to approve ID');
    } finally {
      setApproving(null);
    }
  };

  // Open reject dialog
  const handleRejectClick = (idType: 'primary' | 'secondary') => {
    setRejectingIdType(idType);
    setRejectDialogOpen(true);
  };

  // Handle successful rejection
  const handleRejectSuccess = () => {
    loadData();
  };

  // Calculate completion status
  const hasPrimaryID = !!data.primaryFrontPath;
  const hasSecondaryID = !!data.secondaryFrontPath;
  const primaryApproved = data.primaryStatus === 'approved';
  const secondaryApproved = data.secondaryStatus === 'approved';
  const bothApproved = primaryApproved && secondaryApproved;

  // Progress calculation
  let progressPercent = 0;
  if (hasPrimaryID) {
progressPercent += 25;
}
  if (hasSecondaryID) {
progressPercent += 25;
}
  if (primaryApproved) {
progressPercent += 25;
}
  if (secondaryApproved) {
progressPercent += 25;
}

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--green-700)' }} />
        <span className="ml-2 text-sm text-gray-600">Loading identification documents...</span>
      </div>
    );
  }

  const recruitName = `${data.firstName} ${data.lastName}`;

  return (
    <div className="space-y-4">
      {/* Header with Progress */}
      <div className="flex items-center justify-between">
        <div>
          <div className="dw-sec-title" style={{ marginBottom: 2 }}>Identification Documents</div>
          <p style={{ fontSize: 12, color: 'var(--lw-muted)', margin: 0 }}>Two forms of valid government ID required</p>
        </div>
        <span
          className="badge"
          style={
            bothApproved
              ? { background: '#E2EFE6', color: '#246042' }
              : hasPrimaryID && hasSecondaryID
              ? { background: '#F6EEDA', color: '#7E6426' }
              : { background: '#EEEADD', color: '#6F6A57' }
          }
        >
          {bothApproved ? 'Both Approved' : `${(hasPrimaryID ? 1 : 0) + (hasSecondaryID ? 1 : 0)} of 2 uploaded`}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between" style={{ fontSize: 12, color: 'var(--lw-muted)' }}>
          <span>Verification Progress</span>
          <span style={{ fontWeight: 600 }}>{progressPercent}%</span>
        </div>
        <div style={{ width: '100%', background: 'var(--line-soft)', borderRadius: 999, height: 8 }}>
          <div
            style={{ height: 8, borderRadius: 999, transition: 'all .5s', background: bothApproved ? '#2E8255' : 'var(--green-700)', width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* ID Document Cards */}
      <div className="space-y-3">
        {/* Primary ID Card */}
        <IDCard
          title="Primary ID"
          idType={data.primaryType}
          frontPath={data.primaryFrontPath}
          backPath={data.primaryBackPath}
          status={data.primaryStatus}
          approvedAt={data.primaryApprovedAt}
          rejectedAt={data.primaryRejectedAt}
          rejectionReason={data.primaryRejectionReason}
          onViewDocument={handleViewDocument}
          onApprove={() => handleApprove('primary')}
          onReject={() => handleRejectClick('primary')}
          isApproving={approving === 'primary'}
        />

        {/* Secondary ID Card */}
        <IDCard
          title="Secondary ID"
          idType={data.secondaryType}
          frontPath={data.secondaryFrontPath}
          backPath={data.secondaryBackPath}
          status={data.secondaryStatus}
          approvedAt={data.secondaryApprovedAt}
          rejectedAt={data.secondaryRejectedAt}
          rejectionReason={data.secondaryRejectionReason}
          onViewDocument={handleViewDocument}
          onApprove={() => handleApprove('secondary')}
          onReject={() => handleRejectClick('secondary')}
          isApproving={approving === 'secondary'}
        />
      </div>

      {/* Other Documents Section */}
      {data.otherDocuments.length > 0 && (
        <div className="panel" style={{ padding: 16, background: 'var(--cream-2)' }}>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4" style={{ color: 'var(--lw-muted)' }} />
            <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-900)', margin: 0 }}>Other Documents</h4>
            <span style={{ fontSize: 11.5, color: 'var(--lw-muted)' }}>({data.otherDocuments.length})</span>
          </div>
          <div className="space-y-2">
            {data.otherDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between"
                style={{ padding: 8, background: 'var(--lw-card)', borderRadius: 8, border: '1px solid var(--line)' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: '#2E8255' }} />
                  <div className="min-w-0">
                    <p style={{ fontSize: 13, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label}</p>
                    <p style={{ fontSize: 11.5, color: 'var(--lw-muted)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewDocument(doc.file_path)}
                  className="text-xs flex-shrink-0"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Both Approved Badge */}
      {bothApproved && (
        <div style={{ background: '#E9EFE7', border: '1px solid #BFD8C6', borderRadius: 9, padding: 12 }}>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: '#2E8255' }} />
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#246042', margin: 0 }}>
                Identification Verified
              </p>
              <p style={{ fontSize: 12, color: '#3A6B4E', margin: 0 }}>
                Both forms of ID have been approved
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!hasPrimaryID || !hasSecondaryID ? (
        <div style={{ background: '#F7F0DC', border: '1px solid #E4D2A0', borderRadius: 9, padding: 12 }}>
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#8A6420' }} />
            <p style={{ fontSize: 12, color: '#7E6426', margin: 0 }}>
              ID documents are uploaded by the recruit during their onboarding process.
              Once both forms of identification are uploaded, you can review and approve them.
            </p>
          </div>
        </div>
      ) : !bothApproved && (
        <div className="panel" style={{ padding: 12 }}>
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--gold-600)' }} />
            <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: 0 }}>
              Review each identification document carefully.
              Approve valid IDs or reject with a reason if there are issues.
            </p>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      <RejectIDDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        idType={rejectingIdType}
        recruitId={recruitId}
        recruitName={recruitName}
        recruitEmail={data.email}
        onRejectSuccess={handleRejectSuccess}
      />
    </div>
  );
};

// ============================================================================
// ID Card Sub-Component
// ============================================================================

interface IDCardProps {
  title: string;
  idType: IDType | null;
  frontPath: string | null;
  backPath: string | null;
  status: IDStatus | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  onViewDocument: (path: string | null) => void;
  onApprove: () => void;
  onReject: () => void;
  isApproving: boolean;
}

const IDCard: React.FC<IDCardProps> = ({
  title,
  idType,
  frontPath,
  backPath,
  status,
  approvedAt,
  rejectionReason,
  onViewDocument,
  onApprove,
  onReject,
  isApproving
}) => {
  const hasUpload = !!frontPath;
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isPending = status === 'pending' || (!status && hasUpload);

  // Determine card styling
  let cardStyle: React.CSSProperties = { padding: 16 };
  if (isApproved) {
    cardStyle = { padding: 16, background: '#E9EFE7', borderColor: '#BFD8C6' };
  } else if (isRejected) {
    cardStyle = { padding: 16, background: '#F6EBE5', borderColor: '#E3C4B6' };
  } else if (!hasUpload) {
    cardStyle = { padding: 16, background: 'var(--cream-2)' };
  }

  return (
    <div className="panel" style={cardStyle}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {/* Status Icon */}
          <div style={{ padding: 8, borderRadius: 999, background: isApproved ? '#D6E7DC' : isRejected ? '#EDD9D0' : hasUpload ? '#F1E6C4' : 'var(--cream-2)' }}>
            {isApproved ? (
              <CheckCircle className="h-4 w-4" style={{ color: '#2E8255' }} />
            ) : isRejected ? (
              <XCircle className="h-4 w-4" style={{ color: 'var(--danger)' }} />
            ) : hasUpload ? (
              <Clock className="h-4 w-4" style={{ color: '#8A6420' }} />
            ) : (
              <Clock className="h-4 w-4" style={{ color: 'var(--lw-muted)' }} />
            )}
          </div>

          {/* Title and Type */}
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">{title}</h4>
              {/* Status Badge */}
              {isApproved && (
                <span className="badge" style={{ background: '#E2EFE6', color: '#246042', height: 21, fontSize: 11 }}>Approved</span>
              )}
              {isRejected && (
                <span className="badge" style={{ background: '#F0E7E2', color: '#8A5A47', height: 21, fontSize: 11 }}>Rejected</span>
              )}
              {isPending && hasUpload && (
                <span className="badge" style={{ background: '#F6EEDA', color: '#7E6426', height: 21, fontSize: 11 }}>Pending Review</span>
              )}
            </div>
            {idType ? (
              <p className="text-xs text-gray-600 mt-0.5">{getIDLabel(idType)}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">Not uploaded yet</p>
            )}
          </div>
        </div>

        {/* View Buttons */}
        {hasUpload && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDocument(frontPath)}
              className="text-xs"
            >
              <FileText className="h-3 w-3 mr-1" />
              Front
            </Button>
            {backPath && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDocument(backPath)}
                className="text-xs"
              >
                <FileText className="h-3 w-3 mr-1" />
                Back
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Approval Info */}
      {isApproved && approvedAt && (
        <p style={{ fontSize: 12, color: '#3A6B4E', marginTop: 8, marginLeft: 44 }}>
          Approved on {new Date(approvedAt).toLocaleDateString()}
        </p>
      )}

      {/* Rejection Info */}
      {isRejected && (
        <div style={{ marginTop: 12, marginLeft: 44, padding: 8, background: '#F6EBE5', borderRadius: 8 }}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: 'var(--danger)' }} />
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#7E3B27', margin: 0 }}>Rejected</p>
              {rejectionReason && (
                <p style={{ fontSize: 12, color: '#8A5A47', marginTop: 2, marginBottom: 0 }}>"{rejectionReason}"</p>
              )}
              <p style={{ fontSize: 12, color: '#9B4A35', marginTop: 4, marginBottom: 0 }}>Awaiting re-upload from recruit</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons - Show for pending uploads */}
      {isPending && hasUpload && !isApproved && !isRejected && (
        <div className="flex items-center gap-2" style={{ marginTop: 12, marginLeft: 44 }}>
          <button type="button" className="btn-sm solid" onClick={onApprove} disabled={isApproving}>
            {isApproving ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle size={13} />}
            Approve
          </button>
          <button type="button" className="btn-sm danger" onClick={onReject}>
            <XCircle size={13} />
            Reject
          </button>
        </div>
      )}
    </div>
  );
};
